/**
 * One judge for a studio draft: exactly the checks compose runs on a setup
 * (`authorSetupWithProblems`, `readComposedSetup`, `composeProblems`), plus
 * the studio's own. A draft the judge passes is one Build can write, because
 * Build writes it through the same functions.
 *
 * Unfinished is not a problem (J2.4): no name, no records yet, or only lists
 * so far are readiness questions, never a refusal. Every problem names the
 * part it is about by part path (`record:sales/field:amount`), so the canvas
 * marks the card and the store can tell a new problem from an old one.
 *
 * Problem messages are English for the model, like compose's; words a person
 * reads come from the catalogue by `code`.
 */
import { COMPOSE_LIMITS, composeProblems, COMPOSED_TEMPLATE_ID, readComposedSetup, type ComposedSetup } from '../compose';
import { authorSetupWithProblems, type SetupDraft } from '../compose-author';
import { boardCardProblems } from './cards';
import { authorStudioLinks } from './draft-links';
import { importProblems } from './draft-imports';
import { roleProblems } from './draft-roles';
import { namesARow } from './mend';
import { notesProblems } from './notes';
import { appMapProblem, STUDIO_LIMITS, type StudioDraft } from './draft';
import { studioPath } from './paths';

export type StudioProblem = { code: string; message: string; path: string; part: string | null };

/** What compose refuses of a setup that is only not finished yet: readiness, not refusal. */
export const STUDIO_UNFINISHED_CODES = ['RECORDS_MISSING', 'MAIN_RECORD_MISSING'] as const;

/** The reader's ceilings the judge says as one `LIMIT` problem of its own instead. */
const CEILING_REFUSALS: ReadonlySet<string> = new Set(['RECORDS_TOO_MANY', 'FIELDS_TOO_MANY', 'LINKS_TOO_MANY']);

/** The name an unnamed draft is judged under: a missing name is the readiness check's, never the judge's. */
const PLACEHOLDER_NAME = 'Untitled';

/**
 * The records of a studio draft as the setup builder's draft, for the author:
 * records in order, every member kept (a tile's `key` rides along; the author
 * reads none of it). Links are authored by `authorStudioLinks`, so none here.
 */
export const toSetupDraft = (draft: StudioDraft, name?: string): SetupDraft => ({
  businessName: name ?? draft.meta.name,
  currency: draft.meta.currency,
  records: draft.records,
  links: [],
});

/**
 * The `ComposedSetup` a draft authors to: its records, its links, and what it
 * cannot hold yet as the setup's unmet needs. Roles, Dashboard cards and
 * imports are not part of a `ComposedSetup`; Build applies them after compose.
 */
export const authorStudio = (draft: StudioDraft): ComposedSetup => {
  const { setup } = authorSetupWithProblems(toSetupDraft(draft));
  const unmetNeeds = draft.notYet
    .slice(0, COMPOSE_LIMITS.unmetNeeds)
    .map(({ level, archetype, need }) => ({ level, archetype, need }));
  return {
    ...setup,
    businessName: draft.meta.name.trim(),
    links: authorStudioLinks(draft).links,
    ...(unmetNeeds.length === 0 ? {} : { unmetNeeds }),
  };
};

/**
 * The cards a composed setup makes: each record's summary cards as compose
 * wrote them (its own, or the two stat cards a record with a measure and no
 * card of its own is given). The one count of cards (F21 of
 * docs/plans/qa-run-1-review.md, S1: the Build sheet said 22 and the Ready
 * card 24): the sheet counts it before Build, and the build result after.
 */
export const composedCardCountOf = (setup: Pick<ComposedSetup, 'spec'>): number =>
  setup.spec.records.reduce(
    (sum, record) =>
      sum +
      record.views.reduce((inner, view) => {
        const tiles = (view.spec as { summaries?: unknown }).summaries;
        return inner + (view.type === 'summary' && Array.isArray(tiles) ? tiles.length : 0);
      }, 0),
    0,
  );

/** The cards Build makes of a draft: what `composedCardCountOf` counts of the setup it authors to. Dashboard cards are not made by Build. */
export const buildCardCountOf = (draft: StudioDraft): number => composedCardCountOf(authorStudio(draft));

const RECORD_PATH = /^composed\.records\[([^\]]+)\](.*)$/;
const FIELD_STEP = /^\.fields\[([^\]]+)\]/;
const LINES_STEP = /^\.lines/;

/**
 * The part a problem's path is about. A compose path (`composed.records[r]
 * .fields[f].lines.fields[g]`) becomes the field's part path
 * (`record:r/field:f/field:g`); anything else under a record, the record;
 * `composed.links[i]`, the link's key; `composed` alone, the workspace; a
 * path already in part grammar, itself. Null otherwise.
 */
export const studioPartOfProblem = (draft: StudioDraft, path: string): string | null => {
  if (path === COMPOSED_TEMPLATE_ID) return 'meta';
  const link = /^composed\.links\[(\d+)\]/.exec(path);
  if (link !== null) {
    const held = draft.links[Number(link[1])];
    return held === undefined ? null : `link:${held.key}`;
  }
  const record = RECORD_PATH.exec(path);
  if (record !== null) {
    const recordKey = record[1]!;
    let rest = record[2]!;
    const fields: string[] = [];
    for (;;) {
      const step = FIELD_STEP.exec(rest);
      if (step === null) break;
      fields.push(step[1]!);
      rest = rest.slice(step[0].length);
      // A line field goes on down: `.lines.fields[g]`. A problem with the lines
      // record itself (`.lines` and nothing more) is the lines field's.
      const lines = LINES_STEP.exec(rest);
      if (lines === null) break;
      rest = rest.slice(lines[0].length);
    }
    if (fields.length === 0) return `record:${recordKey}`;
    const field = fields.pop()!;
    return studioPath({ kind: 'field', record: recordKey, ...(fields.length === 0 ? {} : { within: fields }), field });
  }
  if (/^(meta|\*|(record|link|board|role|import|source|note|not_yet|gap|app_map):.+)$/.test(path)) return path;
  return null;
};

/**
 * One LIMIT problem: its message ends on the name of the ceiling in
 * `STUDIO_LIMITS`, in brackets, which `studioLimitOf` reads back.
 */
const limitProblem = (what: string, held: number, name: keyof typeof STUDIO_LIMITS, path: string): Omit<StudioProblem, 'part'> => ({
  code: 'LIMIT',
  message: `${what}: ${held}, over the ceiling of ${STUDIO_LIMITS[name]} (${name}).`,
  path,
});

/** Which ceiling of `STUDIO_LIMITS` a LIMIT problem is about, or null for any other problem. */
export const studioLimitOf = (problem: { code: string; message: string }): keyof typeof STUDIO_LIMITS | null => {
  if (problem.code !== 'LIMIT') return null;
  const name = /\((\w+)\)\.$/.exec(problem.message)?.[1];
  return name !== undefined && name in STUDIO_LIMITS ? (name as keyof typeof STUDIO_LIMITS) : null;
};

/** The limit problems: one per ceiling of `STUDIO_LIMITS` a draft is over. */
const limitProblems = (draft: StudioDraft): Array<Omit<StudioProblem, 'part'>> => {
  const out: Array<Omit<StudioProblem, 'part'>> = [];
  const over = (held: number, name: keyof typeof STUDIO_LIMITS, what: string, path: string): void => {
    if (held > STUDIO_LIMITS[name]) out.push(limitProblem(what, held, name, path));
  };
  over(draft.records.length, 'records', 'Records and lists', 'meta');
  over(draft.links.length, 'links', 'Links', 'meta');
  for (const record of draft.records) {
    const path = `record:${record.key}`;
    over(record.fields.length, 'fields', `Fields on "${record.key}"`, path);
    over(record.tiles?.length ?? 0, 'tilesPerRecord', `Cards on "${record.key}"`, path);
    over(record.seedRows?.length ?? 0, 'seedRows', `Starting rows of "${record.key}"`, `${path}/rows`);
  }
  return out;
};

/** The ceilings of the parts beside the records, each said once at `meta`. */
const partLimitProblems = (draft: StudioDraft): Array<Omit<StudioProblem, 'part'>> =>
  (
    [
      [draft.boardCards.length, 'boardCards', 'Dashboard cards'],
      [draft.roles.length, 'roles', 'Roles'],
      [draft.imports.length, 'imports', 'Imports'],
      [draft.sources.length, 'sources', 'Sources'],
      [draft.notes.length, 'notes', 'Notes'],
      [draft.notYet.length, 'notYet', 'Not-yet items'],
      [draft.checks?.length ?? 0, 'checks', 'Checks'],
      [draft.appMaps?.length ?? 0, 'appMaps', 'App maps'],
    ] as const
  )
    .filter(([held, name]) => held > STUDIO_LIMITS[name])
    .map(([held, name, what]) => limitProblem(what, held, name, 'meta'));

/**
 * What compose would ask of a headless record that Edit never writes
 * (`StudioRecord.headless`: a built record whose rows stand unnamed in the
 * workspace): a title while no field of it can name a row, and, when every
 * field it holds is one the draft keeps out (a rollup, lines), a field.
 * Compose asks them of a record it makes; Build again changes a built record
 * and makes neither. By path (the title binding) or code and path. A record
 * that is not headless, or one that has a field that can name a row, is
 * judged as any other.
 */
const headlessAsks = (draft: StudioDraft): ReadonlySet<string> =>
  new Set(
    draft.records
      .filter((record) => record.headless === true && !namesARow(record.fields))
      .flatMap((record) => {
        const at = `${COMPOSED_TEMPLATE_ID}.records[${record.key}]`;
        return [`${at}.bindings[title]`, `BINDING_TITLE_MISSING\u0000${at}`, ...(record.fields.length === 0 ? [`FIELDS_MISSING\u0000${at}`] : [])];
      }),
  );

/**
 * Every problem with a draft, at once: what the author could not place, what
 * the compose reader refuses (one problem, at `composed`), else everything
 * `composeProblems` finds; then the links' own and the limits; then the
 * Dashboard cards, roles, imports, sources, notes, not-yet items, provenance
 * and dismissed gaps (whose paths are already part paths). Never the
 * unfinished codes. Each problem names its part.
 */
export const studioProblems = (draft: StudioDraft): StudioProblem[] => {
  const authored = authorSetupWithProblems(toSetupDraft(draft, draft.meta.name.trim() || PLACEHOLDER_NAME));
  const links = authorStudioLinks(draft);
  const limits = limitProblems(draft);
  const found: Array<Omit<StudioProblem, 'part'>> = [...authored.problems];

  // No records yet is unfinished, and compose would only say so (and refuse
  // the empty description the author writes for it), so it is not asked.
  const read = draft.records.length === 0 ? null : readComposedSetup({ ...authored.setup, links: links.links });
  if (read === null) {
    // Nothing to judge but the links and the limits.
  } else if (!read.ok) {
    // A ceiling the limits already say is said once, as LIMIT.
    if (!(CEILING_REFUSALS.has(read.refusal.code) && limits.length > 0)) {
      found.push({ code: read.refusal.code, message: read.refusal.message, path: COMPOSED_TEMPLATE_ID });
    }
  } else {
    found.push(...composeProblems(read.value));
  }
  found.push(...links.problems, ...limits);
  for (const card of draft.boardCards) found.push(...boardCardProblems(card, draft));
  for (const role of draft.roles) found.push(...roleProblems(role, draft));
  for (const imp of draft.imports) found.push(...importProblems(imp, draft));
  found.push(...notesProblems(draft), ...partLimitProblems(draft));
  for (const map of draft.appMaps ?? []) {
    // A not-yet key is judged as the map is set: one removed since is drawn as left out, never a problem of the draft.
    const problem = appMapProblem(draft, map, { keys: false });
    if (problem !== null) found.push({ code: 'APP_MAP_SHAPE', message: problem, path: `app_map:${map.id}` });
  }

  const unfinished: ReadonlySet<string> = new Set(STUDIO_UNFINISHED_CODES);
  const unasked = headlessAsks(draft);
  return found
    .filter((problem) => !unfinished.has(problem.code) && !unasked.has(problem.path) && !unasked.has(`${problem.code}\u0000${problem.path}`))
    .map((problem) => ({
      code: problem.code,
      message: problem.message,
      path: problem.path,
      part: studioPartOfProblem(draft, problem.path),
    }));
};

const LINK_AT = /^composed\.links\[\d+\]/;

/**
 * A problem's code and path, with a link's place in the list (`composed
 * .links[1]`) said as its key (`link:paidback`): a link's index moves when an
 * earlier link goes, and its key never does.
 */
const problemKey = (problem: StudioProblem): string => {
  const path = problem.part?.startsWith('link:') === true ? problem.path.replace(LINK_AT, problem.part) : problem.path;
  return `${problem.code}\u0000${path}`;
};

/**
 * The problems of `after` that `before` did not have, by code and path (a
 * link's by its key, not its place): the accept rule (J2.3). A batch may not
 * bring a problem the version before it did not have; a problem it carried
 * already becomes a gap.
 */
export const newStudioProblems = (before: readonly StudioProblem[], after: readonly StudioProblem[]): StudioProblem[] => {
  const had = new Set(before.map(problemKey));
  return after.filter((problem) => !had.has(problemKey(problem)));
};
